import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { POI } from '../interfaces/map-data';
import LayerColorUtils from '../utils/layer-color.utils';
import { FootprintIconPath } from '../constants/constants';
import { SanitizationService } from '@shared/services/sanitization.service';

export const AddStopElClass = 'ski-add-stop-btn';

const FootprintIconSvg = `<svg width="14" height="14" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="${FootprintIconPath}" fill="currentColor"/>
</svg>`;

@Injectable({ providedIn: 'root' })
export class MapTemplateService {
  private translate = inject(TranslateService);
  private sanitizationService = inject(SanitizationService);

  getMaintainerOrNetworkPopup(
    name: string,
    timeAgo: string,
    iconHtml: string,
    customerId?: number,
    iconTextKey?: string,
    showAddStopBtn = false,
  ): string {
    const hasCustomer = !!customerId && customerId !== -1;

    const content = hasCustomer
      ? `
    <div class="network-popup__customer-info">
      ${this.translate.instant(iconTextKey ?? 'General.LastGroomed')}:
      <span class="network-popup__time-ago">${timeAgo}</span>
    </div>
  `
      : `
    <div class="network-popup__no-customer">
      <span class="network-popup__no-customer-text">
        ${this.translate.instant('Map.NoRouteNetworkCustomerText')}
      </span>
      <span class="network-popup__no-customer-link" onclick="window.location.assign('/info')">
        ${this.translate.instant('Map.NoRouteNetworkCustomerLink')}
      </span>
    </div>
  `;

    return `
  <div class="network-popup">
    <div class="network-popup__name">${name}</div>
    <div class="network-popup__divider"></div>
    <div class="network-popup__body">
      ${iconHtml}
      <div class="network-popup__content">${content}</div>
    </div>
    ${
      showAddStopBtn
        ? `
      <div class="network-popup__actions">
        <button class="network-popup__start-tour-btn dv-label-md ${AddStopElClass}">
        <span class="dv-icons">trip</span>${this.translate.instant('Map.StartTourBtnText')}
        </button>
      </div>
    `
        : ''
    }
  </div>
`;
  }

  getPOIPopup(poi: POI): string {
    const sanitizedPurpose = this.sanitizationService.sanitize(poi.Purpose || '');
    const sanitizedDescription = this.sanitizationService.sanitize(poi.POIDescription || '');

    return `
    <div class="poi-popup">
      <div class="poi-popup__description">${sanitizedDescription}</div>
      <div class="poi-popup__purpose">${sanitizedPurpose}</div>
      <div class="poi-popup__divider"></div>
      <div class="poi-popup__body">
        <div class="poi-popup__icon">
          ${LayerColorUtils.getIconWithFill(poi.IconKey || 'icon-warning', poi.IconColor || 'red')}
        </div>
        <div class="poi-popup__meta">
          <div class="poi-popup__type">
            ${poi.POIType_TranslationKey ? this.translate.instant(poi.POIType_TranslationKey) : ''}
          </div>
          <div class="poi-popup__severity">
            ${poi.Severity_TranslationKey ? this.translate.instant(poi.Severity_TranslationKey) : ''}
          </div>
        </div>
      </div>
    </div>
  `;
  }

  buildMaintainerMarkerImg(svgUrl: string, iconSize: number): string {
    return `<img src="${svgUrl}" style="width:${iconSize}px; height:${iconSize}px"/>`;
  }

  buildWalkingSegmentMarkerDiv(borderColor: string): string {
    return `
    <div class="walking-segment-marker" style="border-color:${borderColor}">
         ${FootprintIconSvg}
    </div>
  `;
  }

  buildPOIMarkerDiv(color: string, iconKey: string): string {
    return `
    <div class="poi-marker">
         ${LayerColorUtils.getIconWithFill(iconKey, color)}
    </div>
  `;
  }
}
